/**
 * Dienst der automatischen Sicherung (F-46): startet beim Laden der App,
 * prueft beim Start, bei Rueckkehr in den Tab und stuendlich, ob eine
 * Sicherung faellig ist, und haelt den Stand fuer Einstellungen und Banner.
 *
 * Ohne Nutzeraktion wird die Freigabe nur ABGEFRAGT (`freigabePruefen`).
 * Nachfragen (`freigabeAnfragen`) und die Ordnerauswahl laufen nur in
 * `ordnerWaehlen` und `jetztSichern`, die ausschliesslich aus Klicks kommen.
 * Nie zwei Sicherungen gleichzeitig - auch nicht ueber mehrere Tabs. Jeder
 * Durchlauf liest den Ordner frisch aus der Datenbank: Schaltet ein anderes
 * Fenster aus oder waehlt einen anderen Ordner, gilt das sofort auch hier.
 */
import { create } from 'zustand';

import {
  autoSicherungEntfernen, behaltenLaden, behaltenSpeichern, ordnerLaden, ordnerSpeichern, zuletztLaden, zuletztSpeichern,
} from '@/db/repositories/autoSicherung';
import { behaltenAus, istFaellig, schutzAufheben, type AutoZuletzt } from '@/domain/sicherung/autoSicherung';
import { autoSicherungSchreiben } from '@/lib/backup/autoSicherung';
import {
  dateienAuflisten, fehlerArt, freigabeAnfragen, freigabePruefen, ordnerAuswaehlen, ordnerUnterstuetzt,
  type FehlerArt, type OrdnerDatei, type OrdnerHandle,
} from '@/lib/backup/ordner';
import { today, toLocalDate } from '@/lib/datetime';
import { de } from '@/i18n/de';

export type AutoStatus = 'nicht_verfuegbar' | 'aus' | 'bereit' | 'freigabe' | 'kein_zugriff' | 'laeuft' | 'fehler';

interface AutoStore {
  status: AutoStatus;
  ordnerName: string | null;
  behalten: number;
  zuletzt: AutoZuletzt | null;
  faellig: boolean;
  fehlerArt: FehlerArt | null;
  meldung: string | null;
  /** Automatische Sicherungen im Ordner; `null` ohne Freigabe. */
  dateien: OrdnerDatei[] | null;
}

export const useAutoSicherung = create<AutoStore>(() => ({
  status: ordnerUnterstuetzt() ? 'aus' : 'nicht_verfuegbar',
  ordnerName: null, behalten: behaltenAus(undefined), zuletzt: null, faellig: false,
  fehlerArt: null, meldung: null, dateien: null,
}));

/** Erst nach dem ersten Bild sichern: JSON.stringify grosser Daten blockiert kurz. */
const START_VERZOEGERUNG = 5_000;
const NACH_RUECKKEHR = 2_000;
const INTERVALL = 60 * 60_000;
const SPERRE = 'ole-privat-autosicherung';

let ordner: OrdnerHandle | null = null;
let laeuft: Promise<void> | null = null;
let geplant: ReturnType<typeof setTimeout> | null = null;
let takt: ReturnType<typeof setInterval> | null = null;

const setzen = (s: Partial<AutoStore>) => useAutoSicherung.setState(s);
const tagVon = (z: AutoZuletzt | null) => (z ? toLocalDate(new Date(z.at)) : null);

function planen(ms: number): void {
  if (geplant) clearTimeout(geplant);
  geplant = setTimeout(() => { geplant = null; void autoSicherungPruefen(); }, ms);
}

const beiSichtbar = () => { if (document.visibilityState === 'visible') planen(NACH_RUECKKEHR); };

function ausloeserAn(): void {
  if (takt) return;
  document.addEventListener('visibilitychange', beiSichtbar);
  // Der Tab auf dem Chromebook bleibt oft tagelang offen.
  takt = setInterval(() => { if (document.visibilityState === 'visible') planen(0); }, INTERVALL);
}

function ausloeserAus(): void {
  document.removeEventListener('visibilitychange', beiSichtbar);
  if (takt) clearInterval(takt);
  if (geplant) clearTimeout(geplant);
  takt = geplant = null;
}

/** Nie zwei Durchlaeufe gleichzeitig: ein laufender wird mitgenutzt. */
function einmal(lauf: () => Promise<void>): Promise<void> {
  if (laeuft) return laeuft;
  laeuft = lauf().finally(() => { laeuft = null; });
  return laeuft;
}

/**
 * Fuer Klicks: Ein laufender Durchlauf (z. B. die stuendliche Pruefung) darf
 * den gewuenschten Lauf nicht schlucken - erst abwarten, dann selbst laufen.
 */
async function danach(lauf: () => Promise<void>): Promise<void> {
  while (laeuft) await laeuft.catch(() => undefined);
  return einmal(lauf);
}

/** Ueber Tabs hinweg: Haelt ein anderer Tab die Sperre, laeuft hier nichts. */
async function mitSperre(lauf: () => Promise<void>): Promise<void> {
  const locks = (navigator as { locks?: LockManager }).locks;
  if (!locks?.request) return lauf();
  await locks.request(SPERRE, { ifAvailable: true }, async (lock) => { if (lock) await lauf(); });
}

/** Ein anderes Fenster hat ausgeschaltet: hier ebenfalls aufhoeren. */
function abgeschaltet(): void {
  ausloeserAus();
  ordner = null;
  setzen({ status: 'aus', ordnerName: null, zuletzt: null, faellig: false, fehlerArt: null, meldung: null, dateien: null });
}

/** Den gespeicherten Ordner frisch lesen - `null` heisst: inzwischen ausgeschaltet. */
async function aktuellerOrdner(): Promise<OrdnerHandle | null> {
  const h = await ordnerLaden();
  if (!h) {
    abgeschaltet();
    return null;
  }
  ordner = h;
  if (useAutoSicherung.getState().ordnerName !== h.name) setzen({ ordnerName: h.name, dateien: null });
  return h;
}

/** Zustand fuer eine Freigabe, die (noch) nicht erteilt ist. */
const ohneFreigabe = (f: 'prompt' | 'denied'): Partial<AutoStore> => (f === 'denied'
  ? { status: 'kein_zugriff', fehlerArt: 'freigabe', meldung: null, dateien: null }
  : { status: 'freigabe', fehlerArt: null, meldung: null, dateien: null });

function fehlerZustand(err: unknown): Partial<AutoStore> {
  const art = fehlerArt(err);
  const name = ordner?.name ?? '';
  switch (art) {
    case 'freigabe': return { status: 'freigabe', fehlerArt: art, meldung: null, dateien: null };
    case 'nicht_gefunden': return { status: 'kein_zugriff', fehlerArt: art, meldung: de.backup.auto.notFound(name), dateien: null };
    case 'voll': return { status: 'fehler', fehlerArt: art, meldung: de.backup.auto.full };
    default: return { status: 'fehler', fehlerArt: art, meldung: de.backup.auto.writeFailed };
  }
}

/** Schreibt, wenn faellig oder `erzwingen`. Die Freigabe muss schon da sein. */
async function sichern(erzwingen: boolean): Promise<void> {
  if (!ordner) return;
  setzen({ status: 'laeuft', fehlerArt: null, meldung: null });
  try {
    let meldung: string | null = null;
    let anders: Partial<AutoStore> | null = null;
    await mitSperre(async () => {
      // Erst in der Sperre: Ordner und Stand koennen sich in einem anderen Tab geaendert haben.
      const o = await aktuellerOrdner();
      if (!o) return;
      const freigabe = await freigabePruefen(o);
      if (freigabe !== 'granted') {
        anders = ohneFreigabe(freigabe);
        return;
      }
      // Ein anderer Tab kann eben erst gesichert haben.
      if (!erzwingen && !istFaellig(tagVon(await zuletztLaden()), today())) return;
      // Auch die Anzahl frisch lesen: sie kann in einem anderen Fenster geaendert worden sein.
      const behalten = await behaltenLaden();
      setzen({ behalten });
      const r = await autoSicherungSchreiben(o, { behalten });
      meldung = de.backup.auto.saved(r.datei);
    });
    if (!ordner) return; // inzwischen ausgeschaltet
    if (anders) {
      setzen(anders);
      return;
    }
    const zuletzt = await zuletztLaden();
    setzen({ status: 'bereit', zuletzt, faellig: istFaellig(tagVon(zuletzt), today()), meldung });
  } catch (err) {
    console.warn('Automatische Sicherung:', err);
    setzen(fehlerZustand(err));
  }
}

/** Ohne Nutzeraktion: nur nachsehen, nie nach der Freigabe fragen. Wirft nie. */
export function autoSicherungPruefen(): Promise<void> {
  return einmal(async () => {
    try {
      if (!ordner) return;
      const o = await aktuellerOrdner();
      if (!o) return;
      const zuletzt = await zuletztLaden();
      const faellig = istFaellig(tagVon(zuletzt), today());
      setzen({ zuletzt, faellig });
      const freigabe = await freigabePruefen(o);
      if (freigabe === 'denied') setzen(ohneFreigabe('denied'));
      else if (!faellig) setzen({ status: 'bereit', fehlerArt: null });
      else if (freigabe === 'prompt') setzen(ohneFreigabe('prompt'));
      else await sichern(false);
    } catch (err) {
      console.warn('Automatische Sicherung (Pruefung):', err);
      setzen(fehlerZustand(err));
    }
  });
}

/** Beim App-Start einmal aufrufen. Ohne gewaehlten Ordner passiert nichts. */
export async function autoSicherungInitialisieren(): Promise<void> {
  if (!ordnerUnterstuetzt()) {
    setzen({ status: 'nicht_verfuegbar' });
    return;
  }
  const [h, behalten, zuletzt] = await Promise.all([ordnerLaden(), behaltenLaden(), zuletztLaden()]);
  setzen({ behalten, zuletzt, faellig: istFaellig(tagVon(zuletzt), today()) });
  if (!h) {
    setzen({ status: 'aus' });
    return;
  }
  ordner = h;
  // Die Freigabe gleich abfragen (kostet nichts, fragt nie nach) - sonst
  // wechselten Banner und Einstellungen nach ein paar Sekunden ihre Knoepfe.
  const faellig = istFaellig(tagVon(zuletzt), today());
  const freigabe = await freigabePruefen(h).catch(() => 'granted' as const);
  setzen({
    ordnerName: h.name,
    ...(freigabe === 'denied' ? ohneFreigabe('denied') : faellig && freigabe === 'prompt' ? ohneFreigabe('prompt') : { status: 'bereit' }),
  });
  ausloeserAn();
  // Das Schreiben selbst erst nach dem ersten Bild: JSON.stringify grosser Daten blockiert kurz.
  planen(START_VERZOEGERUNG);
}

/** Ist schon ein Ordner gewaehlt, zeigt die Fehlerbox die Meldung; sonst steht sie unter dem Knopf. */
const auswahlFehler = (): Partial<AutoStore> => (ordner
  ? { status: 'fehler', fehlerArt: 'unbekannt', meldung: de.backup.auto.chooseFailed }
  : { meldung: de.backup.auto.chooseFailed });

/** NUR aus einem Klick: Ordner waehlen und gleich eine erste Sicherung schreiben. */
export async function ordnerWaehlen(): Promise<void> {
  let h: OrdnerHandle | null;
  try {
    h = await ordnerAuswaehlen(ordner ?? undefined);
  } catch (err) {
    console.warn('Ordnerauswahl:', err);
    setzen(auswahlFehler());
    return;
  }
  if (!h) return; // abgebrochen
  try {
    await ordnerSpeichern(h);
  } catch (err) {
    console.warn('Ordner nicht gespeichert:', err);
    setzen(auswahlFehler());
    return;
  }
  ordner = h;
  setzen({ status: 'bereit', ordnerName: h.name, fehlerArt: null, meldung: null, dateien: null });
  ausloeserAn();
  await danach(() => sichern(true));
}

/**
 * NUR aus einem Klick ("Jetzt sichern", "Freigeben"): Freigabe erbitten -
 * als ERSTES await, sonst ist die Nutzeraktion verbraucht - und schreiben.
 */
export async function jetztSichern(): Promise<void> {
  const o = ordner;
  if (!o) return;
  let freigabe: Awaited<ReturnType<typeof freigabeAnfragen>>;
  try {
    freigabe = await freigabeAnfragen(o);
  } catch (err) {
    console.warn('Ordnerfreigabe:', err);
    setzen(fehlerZustand(err));
    return;
  }
  if (freigabe === 'denied') {
    // Nach einer Ablehnung antwortet Chrome oft sofort so; dann hilft nur neu waehlen.
    setzen({ status: 'kein_zugriff', fehlerArt: 'freigabe', meldung: null });
    return;
  }
  if (freigabe === 'prompt') {
    setzen({ status: 'freigabe', fehlerArt: null });
    return;
  }
  await danach(() => sichern(true));
}

/** Gilt ab der naechsten Sicherung. */
export async function behaltenAendern(n: number): Promise<void> {
  const behalten = behaltenAus(n);
  try {
    await behaltenSpeichern(behalten);
    setzen({ behalten });
  } catch (err) {
    console.warn('Anzahl nicht gespeichert:', err);
    setzen({ meldung: de.backup.auto.settingFailed });
  }
}

/** "Neuer Stand stimmt" nach einem Schutzhinweis: ab der naechsten Sicherung wird wieder aufgeraeumt. */
export async function neuenStandBestaetigen(): Promise<void> {
  try {
    const z = await zuletztLaden();
    if (!z) return;
    const neu = schutzAufheben(z);
    await zuletztSpeichern(neu);
    setzen({ zuletzt: neu });
  } catch (err) {
    console.warn('Stand nicht bestaetigt:', err);
    setzen({ meldung: de.backup.auto.settingFailed });
  }
}

/** Ausschalten. Chrome kennt kein Zurueckgeben der Freigabe; den Handle zu vergessen genuegt. */
export async function autoSicherungAus(): Promise<void> {
  if (laeuft) await laeuft.catch(() => undefined);
  try {
    await autoSicherungEntfernen();
  } catch (err) {
    // Nichts halb ausschalten: Ausloeser und Ordner bleiben, bis es geklappt hat.
    console.warn('Automatische Sicherung nicht ausgeschaltet:', err);
    setzen({ meldung: de.backup.auto.settingFailed });
    return;
  }
  ausloeserAus();
  ordner = null;
  setzen({
    status: ordnerUnterstuetzt() ? 'aus' : 'nicht_verfuegbar',
    ordnerName: null, zuletzt: null, faellig: false, fehlerArt: null, meldung: null, dateien: null,
  });
}

/** Dateiliste fuer die Tabelle - nur mit erteilter Freigabe, ohne nachzufragen. */
export async function dateienAktualisieren(): Promise<void> {
  const o = ordner;
  if (!o) {
    setzen({ dateien: null });
    return;
  }
  try {
    if ((await freigabePruefen(o)) !== 'granted') {
      setzen({ dateien: null });
      return;
    }
    const dateien = await dateienAuflisten(o);
    if (ordner === o) setzen({ dateien: dateien.sort((a, b) => b.name.localeCompare(a.name)) });
  } catch (err) {
    console.warn('Dateiliste der Sicherung:', err);
    setzen({ dateien: null });
  }
}
